// ─────────────────────────────────────────────────────────────────────
// lib/newsUnread.js — numerino delle news nuove sulla scheda News
//
// Conta le news pubblicate (dim_news.is_published, published_at ≤ ora) DOPO l'ultima volta che
// l'utente ha aperto la scheda News. Quel momento ("ultima news vista") sta sul dispositivo
// (AsyncStorage, una chiave per utente): nessuna scrittura su database, nessuna tabella nuova.
//
// Alla prima esecuzione non c'è nessun "visto": lo inizializzo all'ultima news già pubblicata, così
// il rilascio non mostra un numero enorme per le news vecchie — solo quelle future fanno comparire il badge.
//
// DIFFERENZA RISPETTO A VB: in VC il tab News contiene anche le notizie esterne dell'aggregatore (decine al giorno, dim_news.is_external):
// contarle terrebbe il numerino sempre acceso. COUNT_EXTERNAL = false → il badge conta solo le notizie scritte da noi.
//
// Uso:
//   const unread = useNewsUnread()          → numero di news nuove (0 = niente badge)
//   markNewsSeen(latestPublishedAt?)        → azzera il badge (la scheda News lo chiama quando si apre)
// ─────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react'
import { AppState } from 'react-native'
import { supabase } from './supabase'
import { storage } from './storage'
import { useAuth } from './AuthContext'

const COUNT_EXTERNAL = false
const REFRESH_MS = 2 * 60 * 1000   // come il conteggio della campanella
const keyOf = (userId) => `news_seen_at_${userId}`

let count = 0
const listeners = new Set()
const setCount = (n) => { count = n; listeners.forEach((fn) => fn(n)) }

// News più recente già pubblicata (published_at), o null
async function latestPublishedAt() {
  const { data, error } = await supabase
    .from('dim_news')
    .select('published_at')
    .eq('is_published', true)
    .in('is_external', COUNT_EXTERNAL ? [true, false] : [false])
    .lte('published_at', new Date().toISOString())
    .order('published_at', { ascending: false })
    .limit(1)
  if (error || !data?.length) return null
  return data[0].published_at
}

export async function refreshNewsUnread(userId) {
  if (!userId) { setCount(0); return }
  try {
    const seen = await storage.get(keyOf(userId))
    if (!seen) {
      // prima volta: considera "viste" tutte le news già pubblicate
      const latest = await latestPublishedAt()
      await storage.set(keyOf(userId), latest ?? new Date().toISOString())
      setCount(0)
      return
    }
    const { count: n, error } = await supabase
      .from('dim_news')
      .select('id', { count: 'exact', head: true })
      .eq('is_published', true)
      .in('is_external', COUNT_EXTERNAL ? [true, false] : [false])
      .gt('published_at', seen)
      .lte('published_at', new Date().toISOString())
    if (!error) setCount(n ?? 0)
  } catch { /* rete assente: il numero resta quello di prima */ }
}

// Chiamata quando l'utente ha visto le news: segna come viste tutte quelle pubblicate finora
export async function markNewsSeen(userId, latest) {
  if (!userId) return
  const at = latest ?? (await latestPublishedAt()) ?? new Date().toISOString()
  await storage.set(keyOf(userId), at)
  setCount(0)
}

export function useNewsUnread() {
  const { user } = useAuth()
  const [n, setN] = useState(count)

  useEffect(() => {
    listeners.add(setN)
    setN(count)
    return () => { listeners.delete(setN) }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    refreshNewsUnread(user.id)
    const timer = setInterval(() => refreshNewsUnread(user.id), REFRESH_MS)
    const sub = AppState.addEventListener('change', (s) => { if (s === 'active') refreshNewsUnread(user.id) })
    return () => { clearInterval(timer); sub.remove() }
  }, [user?.id])

  return n
}
